import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import type { PageEventType } from './eventTypes'
import { useRudderstack } from './useRudderstack'

const DEBOUNCE_TIMEOUT = 200 // ms

type Props = {
  /** The name of the page to track */
  pageName: PageEventType

  /**
   * If the page event is showing, should be used for modals.
   *
   * The default is to assume `isShowing` is true if there is o modal open
   * on the screen.
   */
  isShowing?: boolean
}

export const usePageEventTracking = ({
  pageName,
  isShowing = true,
}: Props): (() => void) => {
  const { page } = useRudderstack()
  const { i18n } = useTranslation()

  const trackPage = (_pageName?: string): void =>
    page({
      category: _pageName ?? pageName,
      name: _pageName ?? pageName,
      properties: {
        url: window.location.href,
        locale: i18n.language,
      },
    })

  useEffect(() => {
    if (!isShowing) {
      return
    }
    const source = setTimeout(() => {
      trackPage()
    }, DEBOUNCE_TIMEOUT)
    return () => clearTimeout(source)
  }, [isShowing, i18n.language])

  return trackPage
}
